import { useState, useEffect, useCallback } from 'react';
import type { AdSlot } from '@/mocks/ads';

interface AdCardProps {
  ads: AdSlot[];
  className?: string;
}

export default function AdCard({ ads, className = '' }: AdCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalAds = ads.length;

  const nextAd = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalAds);
  }, [totalAds]);

  useEffect(() => {
    if (totalAds <= 1) return;
    const interval = setInterval(nextAd, 5000);
    return () => clearInterval(interval);
  }, [totalAds, nextAd]);

  if (!ads.length) return null;

  const currentAd = ads[currentIndex];

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-200 group cursor-pointer">
        <div className="relative">
          <div className="w-full aspect-[1/2] overflow-hidden">
            <img
              src={currentAd.imageUrl}
              alt={currentAd.title}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div className="absolute top-3 left-3">
            <span className="inline-block px-2.5 py-1 bg-foreground-950/70 backdrop-blur-sm text-background-50 text-[10px] font-semibold rounded-md uppercase tracking-wider">
              Publicidad
            </span>
          </div>
          {totalAds > 1 && (
            <div className="absolute bottom-3 right-3">
              <span className="inline-block px-2 py-0.5 bg-foreground-950/60 backdrop-blur-sm text-background-50 text-[10px] font-medium rounded-full">
                Ad {currentIndex + 1}/{totalAds}
              </span>
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="text-foreground-950 font-semibold text-sm mb-1.5 line-clamp-2">
            {currentAd.title}
          </h3>
          <p className="text-foreground-500 text-xs leading-relaxed mb-3 line-clamp-3">
            {currentAd.description}
          </p>
          <a
            href={currentAd.ctaUrl}
            className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-semibold text-xs transition-colors"
          >
            {currentAd.ctaText}
            <i className="ri-arrow-right-line text-xs"></i>
          </a>
        </div>
      </div>

      {totalAds > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {ads.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'bg-primary-500 w-4'
                  : 'bg-foreground-300 hover:bg-foreground-400'
              }`}
              aria-label={`Anuncio ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}